import { createContext, useReducer, useRef, useMemo, useContext } from 'react'
import { fetchMiddleware } from './fetch-middleware.js'

export const createDomain = ({
  domain = 'domain',
  selectors = {},
  actions = {},
  reducer = (state, _action) => state,
  initialState = { data: null, error: null, isLoading: false },
  throwErrorIfNoProvider = true,
}) => {
  let config = {}
  const getConfig = () => config
  const setConfig = (newConfig) => {
    config = { ...config, ...newConfig }
  }

  const DomainContext = createContext({
    dispatch: (_action) => {},
    state: {},
    boundActions: {},
    boundSelectors: {},
    getConfig,
    setConfig,
  })

  const Provider = ({ children, ...props }) => {
    const [state, send] = useReducer(reducer, initialState)
    const stateRef = useRef(state)
    stateRef.current = state

    setConfig(props)
    const { debug } = getConfig()

    const dispatch = useMemo(
      () =>
        fetchMiddleware({
          getState: () => stateRef.current,
          dispatch: (...args) => {
            if (debug) {
              console.group(domain)
              console.group('dispatch')
              console.table(...args)
              console.groupEnd()
              console.groupEnd()
            }
            send(...args)
          },
        })(send),
      [debug]
    )

    const boundSelectors = useMemo(
      () =>
        Object.entries(selectors).reduce((boundSelectors, [selectorName, selector]) => {
          selector.getConfig = getConfig
          boundSelectors[selectorName] = (...args) => selector(stateRef.current, ...args)
          return boundSelectors
        }, {}),
      []
    )

    const boundActions = useMemo(
      () =>
        Object.entries(actions).reduce((boundActions, [actionName, action]) => {
          action.getConfig = getConfig
          boundActions[actionName] = (...args) => dispatch(action(...args))
          return boundActions
        }, {}),
      [dispatch]
    )

    return (
      <DomainContext.Provider
        value={{
          dispatch,
          state: stateRef.current,
          boundSelectors,
          boundActions,
          getConfig,
          setConfig,
        }}
      >
        {children}
      </DomainContext.Provider>
    )
  }

  const useDomainHook = (config = {}) => {
    const { dispatch, state, boundActions, boundSelectors, getConfig } = useContext(DomainContext)
    const getHasDomainProvider = () => !!dispatch
    const hasDomainProvider = getHasDomainProvider()

    if (!hasDomainProvider) {
      if (throwErrorIfNoProvider)
        throw new Error('There must be a DomainProvider component in your render tree to use a domain hook')
      else return { getHasDomainProvider }
    }

    const localConfig = { ...getConfig(), ...config }
    Object.values(boundActions).forEach((action) => {
      action.getConfig = () => localConfig
    })
    Object.values(boundSelectors).forEach((selector) => {
      selector.getConfig = () => localConfig
    })
    const { debug } = getConfig()

    if (debug) {
      console.group(domain)

      console.group('state')
      console.table(state)
      console.groupEnd()

      console.group('selectors')
      console.table(
        Object.entries(boundSelectors).reduce((formatted, [selectorName, selector]) => {
          formatted[selectorName] = { value: selector() }
          return formatted
        }, {})
      )
      console.groupEnd()

      console.group('actions')
      console.table(
        Object.entries(boundActions).reduce((formatted, [actionName, action]) => {
          formatted[actionName] = { value: action }
          return formatted
        }, {})
      )
      console.groupEnd()
      console.groupEnd()
    }

    return {
      ...boundActions,
      ...boundSelectors,
      getHasDomainProvider,
    }
  }

  return [Provider, useDomainHook]
}
